/**
 * iOS Safari Crash Recovery Utilities
 *
 * Handles detection and recovery from WebGL crashes on iOS Safari.
 * Uses sessionStorage to detect if the previous startup crashed,
 * and localStorage to force Canvas fallback after repeated failures.
 */

import { isIOS } from '../renderScale';
import { forceCanvasRenderer, isCanvasForced } from '../config';

// Storage key for tracking startup state
const STARTUP_KEY = 'gameStarting';

/**
 * Check if the previous startup crashed and force Canvas mode if needed.
 * Call this early in the boot process, before creating the Phaser game.
 */
export function handleStartupCrashRecovery(): void {
  // Only apply crash recovery on iOS where WebGL issues are common
  if (!isIOS()) {
    sessionStorage.removeItem(STARTUP_KEY);
    return;
  }

  // Check if previous startup crashed (session wasn't cleared)
  if (sessionStorage.getItem(STARTUP_KEY) === 'true') {
    console.warn('[Hokm] Previous startup crashed - switching to Canvas renderer');
    forceCanvasRenderer();
    sessionStorage.removeItem(STARTUP_KEY);
  }

  // Mark that we're starting up (will be cleared when game is ready)
  sessionStorage.setItem(STARTUP_KEY, 'true');
}

/**
 * Mark startup as complete - call when game is fully loaded and textures are ready.
 * This clears the startup tracking flag so we know the game loaded successfully.
 */
export function markStartupComplete(): void {
  sessionStorage.removeItem(STARTUP_KEY);
  if (isIOS()) {
    console.log('[Hokm] Startup complete on iOS', isCanvasForced() ? '(Canvas mode)' : '(WebGL mode)');
  }
}
